import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionLabel, EditorialMarquee } from "../components/Sections";
import { submitPartner } from "../lib/api";
import { IMAGES, PARTNER_LIST } from "../data";

const REASONS = [
  { n: "01", title: "Premium positioning", body: "Your clients are referred to a boutique manager that reflects well on you, polished, discreet, and quality-focused." },
  { n: "02", title: "Compliance-first", body: "Healthy Homes and licensing handled rigorously, so referred owners are never exposed to preventable risk." },
  { n: "03", title: "Financial discipline", body: "Backgrounds in financial services and business development mean transparent reporting your clients can trust." },
];

const PARTNERS = ["Real estate agents", "Mortgage advisers", "Financial planners", "Accountants", "Property investors"];

export default function Partners() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitPartner(form);
      toast.success("Thanks, we'll be in touch to discuss a partnership.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partners-page">
      <PageHero
        label="Our Partners"
        title="Refer with confidence."
        intro="Kept partners with professionals whose clients own investment property, real estate agents, mortgage advisers, and financial planners. When you refer an owner to us, you refer them to premium, compliance-first stewardship."
        image={IMAGES.marble}
      />

      <EditorialMarquee />

      {/* Trusted partners */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="partner-list-section">
        <div className="max-w-2xl mb-14">
          <SectionLabel>Our trusted partners</SectionLabel>
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-kept-ink leading-tight">
              The professionals we work alongside.
            </h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-kept-ink/10 border border-kept-ink/10">
          {PARTNER_LIST.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-kept-sand h-full p-10 flex flex-col justify-between transition-colors duration-500 hover:bg-kept-ink"
                data-testid={`partner-item-${i}`}
              >
                <div>
                  <div className="kept-label brass-text group-hover:text-kept-brass transition-colors duration-500">{p.role}</div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-5 text-kept-ink group-hover:text-kept-sand transition-colors duration-500 leading-snug">
                    {p.name}
                  </h3>
                </div>
                <div className="mt-10 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground group-hover:text-kept-sand/70 transition-colors duration-500">{p.display}</span>
                  <ArrowUpRight size={18} className="text-kept-ink/40 group-hover:text-kept-brass transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="partner-reasons">
        <div className="max-w-2xl mb-16">
          <SectionLabel>Why refer to Kept</SectionLabel>
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight text-kept-ink leading-tight">
              A referral that reflects your standards.
            </h2>
          </Reveal>
        </div>
        <div className="space-y-px">
          {REASONS.map((r, i) => (
            <Reveal key={r.n} delay={i * 0.06}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start py-10 border-t border-kept-ink/10">
                <div className="md:col-span-2 font-serif text-5xl font-light brass-text">{r.n}</div>
                <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-kept-ink leading-tight">{r.title}</h3>
                <p className="md:col-span-6 text-muted-foreground text-base md:text-lg leading-relaxed">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap gap-3" data-testid="partner-types">
          {PARTNERS.map((p) => (
            <span key={p} className="border border-kept-ink/20 px-5 py-2 kept-label text-kept-ink/70" style={{ fontSize: "0.62rem" }}>{p}</span>
          ))}
        </div>
      </section>

      {/* Partner enquiry form */}
      <section className="bg-kept-stone" data-testid="partner-form-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionLabel>Become a partner</SectionLabel>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-kept-ink leading-tight">
              Let's build a referral relationship.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              Tell us a little about your practice and the owners you work with. We'll reach out to
              discuss how a partnership with Kept works.
            </p>
          </div>
          <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="partner-form">
            <div className="grid md:grid-cols-2 gap-8">
              <input className="kept-input" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="partner-name" />
              <input className="kept-input" placeholder="Email address" type="email" value={form.email} onChange={set("email")} data-testid="partner-email" />
            </div>
            <input className="kept-input" placeholder="Company / practice (optional)" value={form.company} onChange={set("company")} data-testid="partner-company" />
            <textarea className="kept-input resize-none" rows={3} placeholder="Tell us about your referral needs (optional)" value={form.message} onChange={set("message")} data-testid="partner-message" />
            <motion.button
              type="submit"
              disabled={loading}
              whileTap={{ scale: 0.98 }}
              className="group inline-flex items-center gap-3 bg-kept-ink text-kept-brass px-8 py-4 kept-label disabled:opacity-60"
              data-testid="partner-submit"
            >
              {loading ? "Sending…" : "Submit Partner Enquiry"}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </form>
        </div>
      </section>
    </div>
  );
}
